import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

declare global {
  interface Window {
    VKIDSDK?: any;
  }
}

// Версия закреплена: 2.6.6 содержит сломанную UMD-сборку (хелперы @babel/runtime
// вынесены во внешние зависимости и читаются из несуществующих window._typeof и
// window._defineProperty → «TypeError: t is not a function» в exchangeCode).
// Плавающий диапазон @<3.0.0 автоматически подтягивал 2.6.6 и ломал вход.
const VKID_SDK_SRC = 'https://unpkg.com/@vkid/sdk@2.6.5/dist-sdk/umd/index.js';
const VK_API_VERSION = '5.199';
const VK_API_TIMEOUT_MS = 10000;

// Белый список — vk.com/<screen_name>. Сравнение регистронезависимое.
const ALLOWED_SCREEN_NAMES = ['imikarim', 'glamur007', 'id147861691'];

const loadVkidSdk = (): Promise<any> => {
  if (window.VKIDSDK) {
    return Promise.resolve(window.VKIDSDK);
  }
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${VKID_SDK_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(window.VKIDSDK));
      existing.addEventListener('error', reject);
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = VKID_SDK_SRC;
    script.async = true;
    script.onload = () => resolve(window.VKIDSDK);
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

// VK API не отдаёт CORS-заголовки, а access_token привязан к IP браузера —
// серверный прокси невозможен. Используем JSONP: <script>-тег не подчиняется
// CORS-политике, и запрос уходит с того же IP, для которого выдан токен.
const fetchVkScreenName = (accessToken: string): Promise<string | null> =>
  new Promise((resolve) => {
    const callbackName = `__vk_users_get_${Math.floor(Math.random() * 1e12)}`;
    const w = window as unknown as Record<string, unknown>;
    const script = document.createElement('script');

    let settled = false;
    const cleanup = () => {
      delete w[callbackName];
      if (script.parentNode) script.parentNode.removeChild(script);
    };
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };

    w[callbackName] = (data: any) => {
      const screenName = data?.response?.[0]?.screen_name;
      finish(typeof screenName === 'string' ? screenName : null);
    };

    script.src =
      `https://api.vk.com/method/users.get?fields=screen_name` +
      `&v=${VK_API_VERSION}` +
      `&access_token=${encodeURIComponent(accessToken)}` +
      `&callback=${callbackName}`;
    script.onerror = () => finish(null);
    document.body.appendChild(script);

    setTimeout(() => finish(null), VK_API_TIMEOUT_MS);
  });

const isAllowed = (screenName: string | null): boolean =>
  !!screenName && ALLOWED_SCREEN_NAMES.includes(screenName.toLowerCase());

export const HomePage: React.FC = () => {
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (isAuthenticated) return;

    let cancelled = false;

    loadVkidSdk()
      .then((VKID) => {
        if (cancelled || !VKID || !widgetContainerRef.current) return;

        VKID.Config.init({
          app: 54608624,
          redirectUrl: 'https://safety.ufadev.ru/work',
          responseMode: VKID.ConfigResponseMode.Callback,
          source: VKID.ConfigSource.LOWCODE,
          scope: '',
        });

        const oneTap = new VKID.OneTap();

        oneTap
          .render({
            container: widgetContainerRef.current,
            showAlternativeLogin: true,
          })
          .on(VKID.WidgetEvents.ERROR, vkidOnError)
          .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, (payload: { code: string; device_id: string }) => {
            const { code, device_id: deviceId } = payload;
            VKID.Auth.exchangeCode(code, deviceId)
              .then(async (data: Record<string, unknown>) => {
                const accessToken = typeof data.access_token === 'string' ? data.access_token : null;
                if (!accessToken) {
                  setDenied(true);
                  return;
                }

                let screenName: string | null = null;
                try {
                  screenName = await fetchVkScreenName(accessToken);
                } catch (e) {
                  vkidOnError(e);
                }

                if (cancelled) return;

                if (!isAllowed(screenName)) {
                  setDenied(true);
                  return;
                }

                login({ ...data, screen_name: screenName });
                navigate('/work', { replace: true });
              })
              .catch(vkidOnError);
          });
      })
      .catch(vkidOnError);

    return () => {
      cancelled = true;
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
    };
  }, [isAuthenticated, login, navigate]);

  if (isAuthenticated) {
    return <Navigate to="/work" replace />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: '20px',
      }}
    >
      <div
        style={{
          fontSize: '48px',
          fontWeight: 'bold',
          color: '#333',
          marginBottom: '40px',
          letterSpacing: '1px',
        }}
      >
        Safety App
      </div>

      {denied && (
        <div
          style={{
            marginBottom: '20px',
            padding: '12px 16px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            maxWidth: '420px',
            textAlign: 'center',
          }}
        >
          Доступ запрещён. Этот аккаунт VK не имеет прав на работу с приложением.
        </div>
      )}

      <div ref={widgetContainerRef} style={{ minWidth: '320px' }} />
    </div>
  );
};

function vkidOnError(_error: unknown) {
  // Обработка ошибки
}
